<script setup lang="ts">
import type { ConsultationDto, Locales, MoveDirection, MoveState, SectionDto } from '@/services/types/api.types';
import { useFormsStore, useSectionsStore } from '@/stores';
import { computed, onMounted, watch } from 'vue';
import { useRouter } from 'vue-router';
import ReorderableTable from '@/components/ReorderableTable.vue';
import { useI18n } from 'vue-i18n';
import { is, useQuasar } from 'quasar';
import CreateSectionDialog from '@/components/dialogs/CreateSectionDialog.vue';
import { getTranslation } from '@/composables/data-display-utils';
import { activeRuleSets, describeConflicts, formTitle, swapConflicts } from '@/helpers/visibilityRules.helper';
import { confirmDelete } from '@/helpers/dialogs.helper';

const sectionsStore = useSectionsStore();
const formsStore = useFormsStore();
const router = useRouter();
const { t } = useI18n();
const $q = useQuasar();

const props = withDefaults(
  defineProps<{
    consultationId: number;
    consultation?: ConsultationDto | null;
    languageEnabled: Locales[];
    isTemplate?: boolean;
  }>(),
  {
    isTemplate: false,
    consultation: null,
  },
);

const sections = computed(() =>
  sectionsStore.getSectionsByConsultationId(props.consultationId).sort((a, b) => a.order_num - b.order_num),
);
const columns = computed(() => [
  {
    name: 'title',
    label: t('global.label.title'),
    align: 'left',
    field: (row: SectionDto) => getTranslation(row, 'title', props.languageEnabled),
  },
]);

const ruleSets = computed(() => activeRuleSets(props.consultation));

onMounted(async () => {
  await sectionsStore.fetchSectionsByConsultation(props.consultationId);
});

// The questions of every section, to tell which moves the rules refuse: only needed while some rule applies.
// Keyed on the set of sections, not their order, so a move does not reload them.
watch(
  () =>
    ruleSets.value.length > 0
      ? sections.value
          .map((section) => section.id)
          .sort()
          .join(',')
      : '',
  async (sectionIds) => {
    if (!sectionIds) return;
    await Promise.all(sectionIds.split(',').map((id) => formsStore.fetchFormsBySection(Number(id))));
  },
  { immediate: true },
);

// A swap moves every question of one section past every question of the other.
const getMoveState = (row: SectionDto, direction: MoveDirection): MoveState => {
  const index = sections.value.findIndex((section) => section.id === row.id);
  const otherIndex = index + (direction === 'up' ? -1 : 1);
  if (!sections.value[otherIndex]) return { isBlocked: false };
  const sectionForms = sections.value.map((section) =>
    formsStore.getFormsBySectionId(section.id!).sort((a, b) => a.order_num - b.order_num),
  );
  const conflicts = swapConflicts(ruleSets.value, sectionForms, index, otherIndex);
  const allForms = sectionForms.flat();
  return conflicts.length > 0
    ? { isBlocked: true, reason: describeConflicts(conflicts, (id) => formTitle(allForms, id, props.languageEnabled)) }
    : { isBlocked: false };
};

const handleSectionRowClicked = (row: SectionDto) => {
  if (props.isTemplate) {
    router.push({
      name: 'section_template_details',
      params: { consultationId: props.consultationId, id: row.id },
    });
  } else {
    router.push({ name: 'section_details', params: { id: row.id } });
  }
};

const handleSectionReorder = (source: SectionDto, target: SectionDto) => {
  // TODO: this should be atomic
  sectionsStore.saveSection(source);
  sectionsStore.saveSection(target);
};

const handleSectionAddRow = () => {
  const section = sectionsStore.createSection(props.consultationId);
  $q.dialog({
    component: CreateSectionDialog,
    componentProps: {
      consultationId: props.consultationId,
      section,
      languageEnabled: props.languageEnabled,
      isTemplate: props.isTemplate,
    },
  });
};

const handleSectionDeleteRow = (row: SectionDto) => {
  confirmDelete(t('section.name')).onOk(() => {
    sectionsStore.deleteSection(row);
  });
};
</script>

<template>
  <div>
    <reorderable-table
      :rows="sections"
      :columns="columns"
      title="Sections"
      itemKey="id"
      :get-move-state="getMoveState"
      @rowClick="handleSectionRowClicked"
      @reorder="handleSectionReorder"
      @add-row="handleSectionAddRow"
      @delete-row="handleSectionDeleteRow"
    />
  </div>
</template>
